import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Play, CheckSquare, Trash2, Plus, Target, TrendingDown, Users, Timer } from "lucide-react";
import { toast } from "sonner";
import { useRealtime } from "@/lib/realtime";
import { initials } from "@/lib/meta";
import { cn } from "@/lib/utils";

export default function SprintsTab({ project, sprints, issues, reload }) {
  const { users } = useApp();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", goal: "", start_date: "", end_date: "" });
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [burndown, setBurndown] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [timeTracking, setTimeTracking] = useState(null);

  useEffect(() => {
    const active = sprints.find(s => s.state === "active");
    if (active && !selectedSprint) setSelectedSprint(active.id);
  }, [sprints, selectedSprint]);

  const loadSprintExtras = async () => {
    if (!selectedSprint) { setBurndown(null); setCapacity(null); setTimeTracking(null); return; }
    const [b, c, t] = await Promise.all([
      api.get(`/sprints/${selectedSprint}/burndown`),
      api.get(`/sprints/${selectedSprint}/capacity`),
      api.get(`/sprints/${selectedSprint}/timetracking`),
    ]);
    setBurndown(b.data); setCapacity(c.data); setTimeTracking(t.data);
  };

  useEffect(() => { loadSprintExtras(); /* eslint-disable-next-line */ }, [selectedSprint, issues.length]);

  useRealtime((ev) => {
    if (!selectedSprint) return;
    if (ev.project_id !== project.id) return;
    if (["issue.updated","issue.created","issue.deleted","sprint.updated","sprint.completed","worklog.new"].includes(ev.type)) {
      loadSprintExtras();
    }
  }, [selectedSprint, project.id]);

  const submit = async () => {
    if (!form.name.trim()) return;
    await api.post("/sprints", {
      project_id: project.id, name: form.name, goal: form.goal,
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
    });
    toast.success("Sprint created"); setOpen(false); setForm({ name: "", goal: "", start_date: "", end_date: "" }); reload();
  };

  const startSprint = async (id) => {
    await api.patch(`/sprints/${id}`, { state: "active", start_date: new Date().toISOString() });
    toast.success("Sprint started"); reload();
  };
  const completeSprint = async (id) => {
    if (!window.confirm("Complete sprint? Incomplete issues will return to the backlog.")) return;
    await api.post(`/sprints/${id}/complete`);
    toast.success("Sprint completed"); reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete sprint?")) return;
    await api.delete(`/sprints/${id}`); reload();
  };
  const patchSprint = async (id, patch) => {
    await api.patch(`/sprints/${id}`, patch); reload();
  };

  const selected = sprints.find(s => s.id === selectedSprint);

  return (
    <div className="p-6 max-w-[1400px] grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-lg font-semibold">Sprints</h2>
          <Button size="sm" onClick={()=>setOpen(true)} data-testid="new-sprint-btn"><Plus className="w-4 h-4" /></Button>
        </div>
        {sprints.map(s => {
          const cnt = issues.filter(i => i.sprint_id === s.id).length;
          return (
            <button key={s.id} onClick={()=>setSelectedSprint(s.id)} data-testid={`sprint-item-${s.id}`}
              className={cn("w-full text-left border rounded-md p-3 hover:border-primary/50 transition-colors",
                selectedSprint === s.id ? "border-primary bg-primary/5" : "border-border")}>
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium truncate flex-1">{s.name}</div>
                <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-mono uppercase shrink-0",
                  s.state==="active" ? "bg-emerald-500/10 text-emerald-500" :
                  s.state==="closed" ? "bg-slate-500/10 text-slate-500" : "bg-secondary text-muted-foreground")}>{s.state}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 truncate">{s.goal || "No goal"}</div>
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">{cnt} issues</div>
            </button>
          );
        })}
        {sprints.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">No sprints yet</div>}
      </div>

      <div className="space-y-4">
        {selected ? (
          <>
            <Card className="p-5">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-mono uppercase",
                      selected.state==="active" ? "bg-emerald-500/10 text-emerald-500" :
                      selected.state==="closed" ? "bg-slate-500/10 text-slate-500" : "bg-secondary text-muted-foreground")}>{selected.state}</span>
                  </div>
                  <input type="text" value={selected.name} onChange={(e)=>patchSprint(selected.id, {name: e.target.value})}
                    className="font-display text-2xl font-semibold tracking-tight bg-transparent border-none outline-none w-full hover:bg-accent/40 rounded px-1 -mx-1" data-testid="sprint-name-input" />
                  <textarea value={selected.goal || ""} onChange={(e)=>patchSprint(selected.id, {goal: e.target.value})}
                    placeholder="Add a sprint goal…" rows={2}
                    className="text-sm text-muted-foreground bg-transparent border-none outline-none w-full mt-1 hover:bg-accent/40 rounded px-1 -mx-1 resize-none" data-testid="sprint-goal-input" />
                </div>
                <div className="flex gap-1">
                  {selected.state === "future" && <Button size="sm" variant="outline" onClick={()=>startSprint(selected.id)} data-testid={`start-sprint-${selected.id}`}><Play className="w-3.5 h-3.5 mr-1" /> Start</Button>}
                  {selected.state === "active" && <Button size="sm" variant="outline" onClick={()=>completeSprint(selected.id)} data-testid={`complete-sprint-${selected.id}`}><CheckSquare className="w-3.5 h-3.5 mr-1" /> Complete</Button>}
                  <Button size="icon" variant="ghost" onClick={()=>del(selected.id)} data-testid={`del-sprint-${selected.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-border">
                <SprintStat label="Points" value={burndown?.total_points ?? 0} />
                <SprintStat label="Done pts" value={burndown?.done_points ?? 0} accent="text-emerald-500" />
                <SprintStat label="Issues" value={burndown?.issues ?? 0} />
                <SprintStat label="Ends" value={selected.end_date ? new Date(selected.end_date).toLocaleDateString(undefined,{month:'short',day:'numeric'}) : "—"} small />
              </div>
            </Card>

            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="w-4 h-4 text-primary" />
                <h3 className="font-display text-base font-semibold">Burndown</h3>
              </div>
              {burndown?.burndown?.length ? (
                <div className="h-64">
                  <ResponsiveContainer>
                    <LineChart data={burndown.burndown}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(d)=>d?.slice(5)} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
                      <Tooltip contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:6,fontSize:12}} />
                      <Legend wrapperStyle={{fontSize:11}} />
                      <Line type="monotone" dataKey="remaining" name="Remaining" stroke="hsl(var(--primary))" strokeWidth={2} dot={{r:3}} />
                      <Line type="monotone" dataKey="ideal" name="Ideal" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <div className="text-sm text-muted-foreground text-center py-6">No burndown data yet</div>}
            </Card>

            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-primary" />
                <h3 className="font-display text-base font-semibold">Team capacity</h3>
              </div>
              {capacity && Object.keys(capacity.by_user).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(capacity.by_user).map(([uid, v]) => {
                    const u = users.find(x => x.id === uid);
                    const pct = v.points > 0 ? (v.done_points / v.points) * 100 : 0;
                    return (
                      <div key={uid} className="flex items-center gap-3" data-testid={`capacity-${uid}`}>
                        {u ? <Avatar className="w-7 h-7"><AvatarImage src={u.avatar_url} /><AvatarFallback className="text-[10px]">{initials(u.name)}</AvatarFallback></Avatar>
                          : <div className="w-7 h-7 rounded-full border border-dashed border-border grid place-items-center text-[10px] text-muted-foreground">?</div>}
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-medium">{u?.name || "Unassigned"}</span>
                            <span className="font-mono text-muted-foreground">{v.done_points} / {v.points} pts · {v.issues} issues</span>
                          </div>
                          <Progress value={pct} className="h-1.5" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <div className="text-sm text-muted-foreground text-center py-6">No issues in this sprint</div>}
            </Card>
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Timer className="w-4 h-4 text-primary" />
                <h3 className="font-display text-base font-semibold">Time tracking · estimated vs actual</h3>
              </div>
              {timeTracking ? (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Estimated</div>
                      <div className="font-display text-2xl font-semibold font-mono">{Math.round((timeTracking.total_estimated || 0) * 10) / 10}h</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Spent</div>
                      <div className="font-display text-2xl font-semibold font-mono text-primary">{Math.round((timeTracking.total_spent || 0) * 10) / 10}h</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Remaining</div>
                      <div className="font-display text-2xl font-semibold font-mono text-emerald-500">{Math.round((timeTracking.total_remaining || 0) * 10) / 10}h</div>
                    </div>
                  </div>
                  {timeTracking.by_user && Object.keys(timeTracking.by_user).length > 0 ? (
                    <div className="space-y-2" data-testid="sprint-time-by-user">
                      {Object.entries(timeTracking.by_user).map(([uid, v]) => {
                        const u = users.find((x) => x.id === uid);
                        const est = Number(v.estimated || 0);
                        const spent = Number(v.spent || 0);
                        const pct = est > 0 ? Math.min(100, Math.round((spent / est) * 100)) : 0;
                        const over = est > 0 && spent > est;
                        return (
                          <div key={uid} className="flex items-center gap-3">
                            {u ? <Avatar className="w-7 h-7"><AvatarImage src={u.avatar_url} /><AvatarFallback className="text-[10px]">{initials(u.name)}</AvatarFallback></Avatar>
                              : <div className="w-7 h-7 rounded-full border border-dashed border-border grid place-items-center text-[10px] text-muted-foreground">?</div>}
                            <div className="flex-1">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="font-medium">{u ? u.name : "Unassigned"}</span>
                                <span className={cn("font-mono", over ? "text-red-500" : "text-muted-foreground")}>{spent}h / {est}h · {v.issues} issues</span>
                              </div>
                              <Progress value={pct} className="h-1.5" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </>
              ) : <div className="text-sm text-muted-foreground text-center py-6">No time data yet</div>}
            </Card>
          </>
        ) : (
          <Card className="p-16 text-center">
            <Target className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
            <div className="font-display text-lg font-semibold">Pick a sprint</div>
            <div className="text-sm text-muted-foreground mt-1">Select a sprint from the left to see its burndown and capacity.</div>
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">New sprint</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Sprint 14" data-testid="ns-name" /></div>
            <div><Label>Goal</Label><Textarea value={form.goal} onChange={(e)=>setForm({...form,goal:e.target.value})} rows={2} placeholder="What are we trying to achieve?" data-testid="ns-goal" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={(e)=>setForm({...form,start_date:e.target.value})} data-testid="ns-start" /></div>
              <div><Label>End</Label><Input type="date" value={form.end_date} onChange={(e)=>setForm({...form,end_date:e.target.value})} data-testid="ns-end" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="ns-submit">Create sprint</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SprintStat({ label, value, accent, small }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{label}</div>
      <div className={cn("font-display font-semibold", small ? "text-base" : "text-2xl", accent)}>{value}</div>
    </div>
  );
}
